import React from "react";
import PropTypes from "prop-types";

/**
 * A flexible box container component for layout purposes.
 *
 * @param {Object} props - The props object.
 * @param {React.ReactNode} props.children - The child elements to render within the container.
 * @param {string} [props.className=""] - Additional CSS classes to apply to the container.
 * @param {string} [props.direction="column"] - The flex direction, either "column" or "row".
 * @param {number} [props.gap=0] - The gap size between items.
 * @param {string} [props.justify="start"] - The justify content alignment. Options: "start", "center", "between", "evenly".
 * @param {string} [props.align="start"] - The align items setting. Options: "start", "center", "between", "evenly".
 *
 * @returns {JSX.Element} The rendered flex container.
 */
const FlexBox = ({
  children,
  className = "",
  direction = "column",
  gap = 0,
  justify = "start",
  align = "start",
  ...props
}) => {
  const _gap = `gap-${gap}`;
  const _direction = direction === "column" ? "flex-column" : "flex-row";

  const _justify =
    {
      start: "justify-content-start",
      center: "justify-content-center",
      between: "justify-content-between",
      evenly: "justify-content-evenly",
    }[justify] || "justify-content-start";

  const _align =
    {
      start: "align-items-start",
      center: "align-items-center",
      between: "align-items-between",
      evenly: "align-items-evenly",
    }[align] || "align-items-start";

  return (
    <div
      className={`d-flex ${_gap} ${_direction} ${_justify} ${_align} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

FlexBox.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  direction: PropTypes.oneOf(["column", "row"]),
  gap: PropTypes.number,
  justify: PropTypes.oneOf(["start", "center", "between", "evenly"]),
  align: PropTypes.oneOf(["start", "center", "between", "evenly"]),
};

export default FlexBox;
